import { z } from 'zod';

// Frontmatter of a "What's new" release entry (`content/releases/<id>/`).
//
// One entry is one folder: `index.mdx` is the English file and the only one
// that carries the structural fields (date, audience, links). The translated
// files (`index.fr.mdx`, …) only carry the text: `title`, `summary`, `cta` and
// the body. `src/modules/product-updates/releases/releaseEntries.ts` merges the two, so a
// translation can never disagree with the English file on who sees the entry.
// `src/modules/product-updates/releases/releaseContent.node-test.ts` enforces that split and
// that every CRM language has a file.
//
// Kept in its own module for the same reasons as `docsFrontmatterSchema.ts`:
// `source.config.ts` may only export collections, and the test imports it.
// What an entry is about: its icon, colour and filter on the What's new page.
export const RELEASE_TOPICS = [
  'aura',
  'listings',
  'buyers',
  'cma',
  'mls',
  'visibility',
  'prospection',
  'general',
] as const;

export const releaseTextSchema = z.object({
  title: z.string().min(1),
  // One or two sentences, shown in the bell and above the body.
  summary: z.string().min(1),
  // Label of the button that opens `path`, e.g. "Open my listings".
  cta: z.string().min(1).optional(),
});

export const releaseAudienceSchema = z.object({
  // Required on purpose, with no default: every entry decides whether
  // white-label tenants see it. `ra` = RealAdvisor tenants only.
  tenants: z.union([z.enum(['all', 'ra']), z.array(z.string().uuid()).min(1)]),
  // ISO country codes of the user's tenant. Omitted = every country.
  countries: z.array(z.string().length(2)).min(1).optional(),
  // Module ids; the user needs at least one. Omitted = no module needed.
  modules: z.array(z.string().min(1)).min(1).optional(),
  // Same vocabulary as the notification center's `audience`.
  recipients: z
    .enum(['everyone', 'team_leaders', 'members'])
    .default('everyone'),
});

export const releaseMetaSchema = releaseAudienceSchema.extend({
  // The day the feature reached production (YYYY-MM-DD). YAML turns an
  // unquoted date into a `Date`, so both spellings are accepted.
  date: z.preprocess(
    value => (value instanceof Date ? value.toISOString().slice(0, 10) : value),
    z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  ),
  // `major` entries are candidates for the spotlight; everything else is
  // `standard` (feed only). Bug fixes and small tweaks get no entry at all.
  importance: z.enum(['major', 'standard']),
  topic: z.enum(RELEASE_TOPICS),
  // Order among the entries of the same day, after `major` ones: higher
  // first. Omitted = 0.
  rank: z.number().int().optional(),
  announce: z.boolean().default(true),
  // Screenshot or mockup shown at the top of the entry, in
  // `public/images/releases/<id>/` (e.g. `/images/releases/mls/map.png`).
  image: z.string().startsWith('/images/releases/').optional(),
  // CRM path the `cta` button opens (without the language prefix).
  path: z.string().startsWith('/').optional(),
  // Agent Academy page, relative to `/docs`, e.g. `cma/transactions`.
  academy: z.string().min(1).optional(),
  // Id of a guided tour registered in `src/modules/product-updates/tours/tourRegistry.ts`.
  tour: z.string().min(1).optional(),
  // For features in a gradual rollout: the entry is shown to everyone, but a
  // user without `module` gets "Request beta access" instead of `cta` / the
  // tour. The button adds `request` to the current URL (e.g. `ai_beta=true`
  // triggers the beta opt-in survey).
  access: z
    .object({
      module: z.string().min(1),
      request: z.string().regex(/^[\w-]+=[\w-]+(&[\w-]+=[\w-]+)*$/),
    })
    .optional(),
});

export type ReleaseText = z.infer<typeof releaseTextSchema>;
export type ReleaseAudience = z.infer<typeof releaseAudienceSchema>;
export type ReleaseMeta = z.infer<typeof releaseMetaSchema>;

// What fumadocs validates each file against: the text is required everywhere,
// the structural fields are optional here and checked strictly on the English
// file by `releaseEntries.ts` (fumadocs cannot tell the files apart).
export const releaseFrontmatterSchema = releaseTextSchema.extend(
  releaseMetaSchema.partial().shape,
);
